import React from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';
import { resolve } from 'library/helpers/path-resolver';
import Dater from 'library/helpers/date';
import { jsonToInputParams } from 'library/util/graph';
import Query from 'library/util/query';
import { BodyText, Header4 } from 'library/components/text-elements';
import { WorkExperience } from 'apps/journey/pages/user-dashboard/components/work-experience';
import { EducationExperience } from 'apps/journey/pages/user-dashboard/components/education-experience';
import { nullScrubber } from 'library/helpers/object/object-cleaner';
import { CloneComponent } from 'apps/pathways/start/components/clone-component';
import {
  ExperienceContainer,
  SectionContainer,
  SectionContent,
  SectionDescription,
  SectionHeader,
} from './styled-my-experience';

const ExperienceSections = [
  {
    section: 'work_histories',
    id: 'work_histories',
    component: WorkExperience,
    header: 'Job Experience',
    description:
      'Experience includes work history, internships, apprenticeships, volunteering, and extracurricular activities. We’ll use your past experience to create your job-specific skills profile to identify transferable skills that map to many industries.',
  },
  {
    section: 'education_histories',
    id: 'education_histories',
    component: EducationExperience,
    header: 'Education',
    description:
      'Your education may be key to selecting your optimal career options. Once you pick a goal career, we may suggest additional certificates or degrees to accelerate your journey to career success.',
  },
];

const millisecondsBeforeSaving = 2500;

class MyExperience extends React.Component {
  constructor() {
    super();
    this.state = {
      alreadyLoaded: false,
      data: {},
      dirtyFields: {},
      savedFields: [],
    };
    this.debounceSave = debounce(this.handleSubmit, millisecondsBeforeSaving);
    this.formRef = React.createRef();
  }

  static getDerivedStateFromProps(props, state) {
    if (state.alreadyLoaded) return null;

    if (props.loading) {
      return {
        [props.section]: true,
        alreadyLoaded: false,
      };
    }

    return {
      [props.section]: true,
      alreadyLoaded: true,
      data: { [props.section]: props?.account[props.section] },
    };
  }

  handleToggle = (e) => {
    const { name } = e.target;

    this.setState(() => {
      const nextState = { ...this.state };
      nextState[name] = !resolve(this.state, `${name}`, true);
      return nextState;
    });
  };

  makeProcficiencyNumbersAgain(obj) {
    for (const att in obj) {
      if (typeof obj[att] == 'object')
        obj[att] = this.makeProcficiencyNumbersAgain(obj[att]);
      if (att === 'proficiency') {
        obj[att] = Number(obj[att]);
      }
    }
    return obj;
  }

  setToEmpty = (object) => {
    for (const att in object) {
      if (typeof object[att] == 'object') {
        this.setToEmpty(object[att]);
      }
      if (object[att] == null) {
        object[att] = '';
      }
    }
    return object;
  };

  handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const { data } = this.state;

    this.setState((nextState) => {
      nextState.loading = true;
      nextState.savedFields = Object.keys(this.state.dirtyFields);
      nextState.dirtyFields = {};
      return nextState;
    });

    const history_input = [];

    for (const history of data[this.props.section]) {
      const nullScrubbedHistory = nullScrubber(history);

      if (!Object.keys(nullScrubbedHistory?.date_range || {}).length) {
        delete nullScrubbedHistory.date_range;
      }

      history_input.push(nullScrubber(history));
    }

    const input = {
      [`no_validation_${this.props.section}`]: history_input,
    };

    for (const att in this.state) {
      if (this.state[att] === false && att !== 'data') {
        input[att] = [];
      }
      if (att === 'skills') {
        input[att] = this.makeProcficiencyNumbersAgain(input[att]);
      }
    }

    delete input.loading;

    try {
      const response = await Query(
        `
          mutation {
            update_account(input: {${jsonToInputParams(input)}}) {
              errors,
              success,
              account {
                education_histories {
                  id
                  description
                  school
                  degree
                  field_of_study
                  date_range {
                    start
                    finish
                  }
                }
                work_histories {
                  company
                  date_range {
                    start
                    finish
                  }
                  description
                  id
                  is_current
                  title
                  city
                }
              }
            }
          }
        `
      );

      const data = resolve(response, 'data/update_account/account', []);

      this.setState({ savedFields: [] });
      this.props.onComplete(data);
    } catch (e) {
      console.error(e);
    }
    this.setState({ loading: false });
  };

  handleDates = (name, value, current) => {
    const dater = new Dater(current);
    const [, duration] = name.split('_');
    dater[`${duration}`] = value;
    return dater.toString();
  };

  clearSection = (section) => {
    const data = Object.assign({}, this.state.data);
    data[`${section}`][0] = {};
    this.setState({ data }, () => {
      this.handleSubmit();
    });
  };

  onChange = (e, key = '', attribute = '') => {
    const data = JSON.parse(JSON.stringify(this.state.data));
    let { name, value } = e.currentTarget;

    if (!value && typeof value !== 'string')
      value == e.currentTarget?.getAttribute('value');

    //contact info description stuff
    if (key === '') {
      data[name] = value;
    } else {
      // if empty array
      if (key === 0 && data[`${attribute}`][key] === undefined) {
        data[`${attribute}`][key] = {};
      }

      // time period handler
      const dateValues = [
        'start_year',
        'start_month',
        'finish_year',
        'finish_month',
      ];
      if (dateValues.includes(name)) {
        const [ends] = name.split('_');
        const current = resolve(
          data,
          `${attribute}/${key}/date_range/${ends}`,
          '--'
        );
        const newDate = this.handleDates(name, value, current);
        if (resolve(data, `${attribute}/${key}`, null) == null) {
          data[`${attribute}`][key] = {};
        }
        if (resolve(data, `${attribute}/${key}/date_range`, null) == null) {
          data[`${attribute}`][key]['date_range'] = {};
        }
        data[`${attribute}`][key]['date_range'][`${ends}`] = newDate;

        // everything else
      } else {
        if (name === 'is_current') {
          data[`${attribute}`][key][name] = !resolve(
            data,
            `${attribute}/${key}/${name}`,
            false
          );
        } else {
          data[`${attribute}`][key][name] = value;
        }
      }
    }

    this.setState((nextState) => {
      nextState.dirtyFields[`${attribute}-${key}-${name}`] = true;
      nextState.data = data;
      return nextState;
    }, this.debounceSave);
  };

  onAdd = (attribute) => {
    const data = Object.assign({}, this.state.data);
    let addition = {};
    data[`${attribute}`].push(addition);
    this.setState({ data });
  };

  onRemove = (attribute, index) => {
    const data = Object.assign({}, this.state.data);
    if (this.state.data[`${attribute}`].length > 0) {
      data[`${attribute}`].splice(index, 1);
    }
    this.setState({ data }, () => {
      this.handleSubmit();
    });
  };

  mapSections = ({ section, component, description, header }) => {
    return (
      <SectionContainer>
        <SectionHeader>
          <Header4>{header}</Header4>
        </SectionHeader>
        <SectionDescription>
          <BodyText tabIndex={0}>{description}</BodyText>
        </SectionDescription>
        <SectionContent>
          <CloneComponent
            savedFields={this.state.savedFields}
            active={true}
            section={section}
            clearSection={this.clearSection}
            onAdd={this.onAdd}
            onRemove={this.onRemove}
            onChange={this.onChange}
            data={resolve(this.state.data, section, [])}
            component={component}
            customButtons
          />
        </SectionContent>
      </SectionContainer>
    );
  };

  render() {
    const { loading, section } = this.props;
    if (loading) return <h1> Loading </h1>;
    const renderSections = ExperienceSections.filter(
      (expSection) => expSection.section === section
    );
    return (
      <ExperienceContainer>
        <form ref={this.formRef} role="form" onSubmit={this.handleSubmit}>
          {renderSections.map(this.mapSections)}
        </form>
      </ExperienceContainer>
    );
  }
}

MyExperience.propTypes = {
  section: PropTypes.string,
  onComplete: PropTypes.func,
  account: PropTypes.object,
};

export default MyExperience;
